import type { StatusTone } from '../../abf/components/StatusChip';
import { ticketNextStep, ticketPosted, ticketWord } from '../ticketWords';
import type { AcquisitionTicket, AgreementReviewRecord } from './agreementReviewApi';
import { assetLane, documentKind, documentWord, formatStamp, readText } from './agreementReviewModel';

export interface ReviewStop {
  title: string;
  detail: string;
  done: boolean;
}

/** Where the next step is carried out. This page records sources; every decision lives in the screen that owns it. */
export type ReviewTarget = 'APPROVE' | 'DOCUMENT_PROCESSING' | 'TICKET' | 'FACILITY';

export interface ReviewJourney {
  eyebrow: string;
  state: { tone: StatusTone; label: string };
  stops: ReviewStop[];
  /** Index of the stop the document is at; -1 when every stop is done. */
  current: number;
  next: { headline: string; reason: string; action: string; target: ReviewTarget };
}

const EYEBROW: Record<string, string> = {
  AGREEMENT: 'Agreement review', AMENDMENT: 'Amendment review', NOTICE: 'Notice review',
  CERTIFICATE: 'Compliance certificate review', TRADE_CONFIRM: 'Trade confirmation review',
};

/** What importing this kind of document does, in the words Document Processing uses for the same button. */
const importStep = (kind: string, lane: string): { title: string; verb: string; effect: string } => {
  if (kind === 'AGREEMENT' && lane === 'LEVERAGED_LOAN') {
    return {
      title: 'Deal defined', verb: 'Import it to define the deal',
      effect: 'Import creates the deal, its tranches, securities, covenants and lender register as reference data. It does not book a purchase.',
    };
  }
  if (kind === 'AMENDMENT') {
    return { title: 'Amendment applied', verb: 'Apply the amendment', effect: 'Applying it changes the loan’s terms from the effective date.' };
  }
  if (kind === 'CERTIFICATE') {
    return { title: 'Covenant tests recorded', verb: 'Import it to record the covenant tests', effect: 'Import records each reported test against the loan’s covenants.' };
  }
  if (kind === 'TRADE_CONFIRM') {
    return { title: 'Matched to the trade', verb: 'Import it to match the trade', effect: 'Import matches the confirmation to its ticket, or drafts the ticket when none exists.' };
  }
  return { title: 'Booked to the loan', verb: 'Book the notice', effect: 'Booking it records the event on the loan and posts it to IBOR.' };
};

const importMessage = (review: AgreementReviewRecord): string | null => {
  const message = review.oms_import_result && (review.oms_import_result as Record<string, unknown>).message;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
};

/**
 * Where a document stands between arriving and taking effect, and the one thing to do next.
 * Everything here is read from the job and its ticket; nothing is decided on this page.
 */
export const reviewJourney = (
  review: AgreementReviewRecord, ticket: AcquisitionTicket | null, sources: { confirmed: number; total: number },
): ReviewJourney => {
  const kind = documentKind(review.document_type);
  const lane = assetLane(review.document_type);
  const word = documentWord(review.document_type, readText(review, ['notice_event', 'event_type']));
  const approval = String(review.approval_status || 'PENDING_REVIEW').toUpperCase();
  const approved = approval === 'APPROVED';
  const rejected = approval === 'REJECTED';
  const refused = String(review.booking_status || '').toUpperCase() === 'BOOKING_FAILED';
  const imported = String(review.booking_status || '').toUpperCase() === 'POSTED'
    || (review.oms_import_result as Record<string, unknown> | null)?.import_status === 'SUCCESS';
  const privateAgreement = kind === 'AGREEMENT' && lane === 'PRIVATE_CREDIT';
  const facilityCode = readText(review, ['loan_identifiers', 'loan_id']);
  const step = importStep(kind, lane);

  const stops: ReviewStop[] = [
    { title: `${word.charAt(0).toUpperCase()}${word.slice(1)} received`, detail: `Document extraction${review.created_at ? ` · ${formatStamp(review.created_at)}` : ''}`, done: true },
    {
      title: 'Review and approve',
      detail: approved ? `Approved${review.reviewed_by ? ` by ${review.reviewed_by}` : ''}${review.reviewed_at ? ` · ${formatStamp(review.reviewed_at)}` : ''}`
        : rejected ? 'Rejected' : `${sources.confirmed} of ${sources.total} sources confirmed`,
      done: approved,
    },
  ];
  if (privateAgreement) {
    const loanId = review.linked_loan_record_id;
    stops.push(
      { title: 'Facility created', detail: loanId ? `${facilityCode || `Loan ${loanId}`} · import to OMS` : 'After approval', done: Boolean(loanId) },
      {
        title: 'Acquisition ticket',
        detail: ticket ? `#${ticket.id} · ${ticketWord(ticket.status)}` : loanId ? 'No ticket found' : 'After import',
        done: Boolean(ticket && ticketPosted(ticket.status)),
      },
    );
  } else {
    stops.push({
      title: step.title,
      detail: imported ? (facilityCode || 'Done') : refused ? 'Refused at booking' : 'After approval',
      done: imported,
    });
  }
  const current = rejected ? 1 : stops.findIndex(stop => !stop.done);
  const eyebrow = `${EYEBROW[kind]} · ${lane === 'LEVERAGED_LOAN' ? 'Leveraged loan' : 'Private credit'}`;
  const build = (state: ReviewJourney['state'], next: ReviewJourney['next']): ReviewJourney => ({ eyebrow, state, stops, current, next });

  if (rejected) {
    return build({ tone: 'critical', label: 'Rejected' }, {
      headline: 'This extraction was rejected',
      reason: `Nothing was booked from it. Upload the ${word} again to start over.`,
      action: 'Open Document Processing', target: 'DOCUMENT_PROCESSING',
    });
  }
  if (!approved) {
    return build({ tone: 'progress', label: 'Awaiting approval' }, {
      headline: `Check the terms against the ${word}, then approve`,
      reason: 'Approval is recorded in Document Processing, where a term can also be corrected first.',
      action: 'Approve in Document Processing', target: 'APPROVE',
    });
  }
  if (refused && !imported) {
    return build({ tone: 'critical', label: 'Booking refused' }, {
      headline: 'Booking was refused',
      reason: review.last_booking_error || 'Open Document Processing to see why and retry.',
      action: 'Open Document Processing', target: 'DOCUMENT_PROCESSING',
    });
  }
  if (privateAgreement) {
    if (!review.linked_loan_record_id) {
      return build({ tone: 'progress', label: 'Approved · not yet imported' }, {
        headline: 'Approved. Import it to create the facility',
        reason: 'Import to OMS creates the facility, its covenants and a draft acquisition ticket.',
        action: 'Import in Document Processing', target: 'DOCUMENT_PROCESSING',
      });
    }
    if (ticket && !ticketPosted(ticket.status)) {
      return build({ tone: 'progress', label: `Facility created · ticket ${ticketWord(ticket.status).toLowerCase()}` }, {
        headline: `Acquisition ticket #${ticket.id} is ${ticketWord(ticket.status).toLowerCase()}`,
        reason: `${ticketNextStep(ticket.status, ticket.portfolioId != null)} The commitment reaches IBOR when the ticket posts.`,
        action: 'Open the ticket in Blotter', target: 'TICKET',
      });
    }
    return build({ tone: 'done', label: ticket ? 'Posted to IBOR' : 'Facility created' }, {
      headline: ticket ? 'The commitment is posted to IBOR' : 'The facility is created',
      reason: 'Servicing, covenants and funding continue on the facility.',
      action: 'Open the facility', target: 'FACILITY',
    });
  }
  if (!imported) {
    return build({ tone: 'progress', label: 'Approved · not yet imported' }, {
      headline: `Approved. ${step.verb}`,
      reason: step.effect,
      action: 'Open Document Processing', target: 'DOCUMENT_PROCESSING',
    });
  }
  return build({ tone: 'done', label: step.title }, {
    headline: step.title,
    reason: importMessage(review) || (kind === 'AGREEMENT'
      ? 'A purchase is booked from a trade confirmation or an acquisition ticket.'
      : 'The loan carries the result.'),
    action: review.linked_loan_record_id ? 'Open the loan' : 'Open Document Processing',
    target: review.linked_loan_record_id ? 'FACILITY' : 'DOCUMENT_PROCESSING',
  });
};
